import { FC, useCallback, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { SelectorOption } from 'types';

import AsyncSelect from 'react-select/async';
import { CustomAsyncSelectOption } from './types/CustomAsyncSelectOption';
import { useLoadOptions } from './hooks/useLoadOptions';

type CustomAsyncSelectProps = {
  id?: string;
  defaultOptions?: CustomAsyncSelectOption[];
  onChange: (selectedOption: CustomAsyncSelectOption | null) => void; // eslint-disable-line
  onFetchData: (
    searchTerm: string // eslint-disable-line
  ) => Promise<CustomAsyncSelectOption[] | undefined>;
  value?: CustomAsyncSelectOption | null;
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  className?: string;
  minimumInputLength?: number;
  maximumInputLength?: number;
  addOption?: SelectorOption;
  noDataLabelKey?: string;
  loadingLabelKey?: string;
  errorLabelKey?: string;
};

export const CustomAsyncSelect: FC<CustomAsyncSelectProps> = ({
  onChange,
  onFetchData,
  value,
  placeholder = '',
  isDisabled = false,
  isClearable = true,
  className = 'react-select-lg',
  minimumInputLength = 0,
  maximumInputLength = 50,
  defaultOptions = [],
  id,
  addOption,
  noDataLabelKey = 'NoDataFound',
  loadingLabelKey = 'LoadingData',
  errorLabelKey = 'ErrorLoadingData',
}) => {
  const { t } = useTranslate();

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(
    async (searchTerm: string): Promise<CustomAsyncSelectOption[]> => {
      try {
        setIsLoading(true);
        const response = onFetchData ? await onFetchData(searchTerm) : [];

        return response || [];
      } catch {
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [onFetchData]
  );

  const { actions: loadActions } = useLoadOptions({
    minimumInputLength,
    maximumInputLength,
    defaultOptions,
    addOption,
    noDataLabel: t(noDataLabelKey),
    errorLabel: t(errorLabelKey),
    fetchData,
  });

  const handleInputChange = (newInput: string) => {
    if (newInput.length > maximumInputLength) {
      return inputValue;
    }
    setInputValue(newInput);
    return newInput;
  };

  const handleChange = (selectedOption: CustomAsyncSelectOption | null) => {
    onChange(selectedOption);
  };

  return (
    <AsyncSelect<CustomAsyncSelectOption>
      id={id}
      loadOptions={loadActions.loadOptions}
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isClearable={isClearable}
      className={`react-select ${className}`}
      defaultOptions={[...defaultOptions, ...(addOption ? [addOption] : [])]}
      noOptionsMessage={({ inputValue }) => {
        if (isLoading) return t(loadingLabelKey);
        if (inputValue.length < minimumInputLength) {
          return `${t('TypeAtLeastCharacters', {
            charactersCount: minimumInputLength,
          })}`;
        }
        return `${t(noDataLabelKey)}`;
      }}
      styles={{
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
      classNames={{
        option: ({ data }) => data?.className || '',
      }}
    />
  );
};

export default CustomAsyncSelect;
